import { useRef, useState } from 'react';
import Icon, { IconType } from 'components/Icon';
import SmallIconButton, { SmallIconButtonState } from '../Button/SmallIconButton';
import * as S from './styles';
import { Attachment } from 'dragonmouse-shared/interfaces';
import { Loader } from 'components/Widget/Loader';
import { useTheme } from 'styled-components';
import { VITE_FORGE_URL } from '@constants';

export enum AttachmentTileState {
	REGULAR,
	LOADING,
}

const PASTED_FILENAME_RE = /^pasted(-\d+)?\.\w+$/;

interface AttachmentTileProps {
	attachment: Attachment;
	onDelete?: () => void;
	state: AttachmentTileState;
	previewUrl?: string;
	onPreview?: () => void;
}

const AttachmentTile = ({
	attachment,
	onDelete,
	state,
	previewUrl,
	onPreview,
}: AttachmentTileProps) => {
	const theme = useTheme();
	const [isHovered, setIsHovered] = useState(false);
	const ref = useRef(null);

	if (!attachment) {
		console.error("thumbnail being shown for frame that doesn't exist");
		return null;
	}

	const isImage = attachment.contentType.startsWith('image/');
	const attachmentUrl = `${VITE_FORGE_URL}/attachment/${encodeURIComponent(
		attachment.storagePath
	)}`;

	const onClick = () => {
		if (onPreview) {
			onPreview();
		} else if (state !== AttachmentTileState.LOADING) {
			window.open(attachmentUrl, '_blank', 'noopener,noreferrer');
		}
	};

	const onDeleteClick = () => {
		onDelete?.();
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderLoadingIndicator = () => {
		return (
			<S.Loader>
				<Loader size={24} />
			</S.Loader>
		);
	};

	const renderImageThumbnail = (src: string | undefined) => {
		if (!src) return renderLoadingIndicator();
		return <S.ThumbnailImage src={src} alt={attachment.filename} />;
	};

	const renderIcon = () => {
		return (
			<S.Icon>
				<Icon type={IconType.FILE} width={26} height={26} color={theme.gray[700]} />
			</S.Icon>
		);
	};

	const renderLeftContent = () => {
		if (state === AttachmentTileState.LOADING) {
			if (isImage && previewUrl) {
				return (
					<>
						<S.ThumbnailImage src={previewUrl} alt={attachment.filename} />
						<S.LoaderOverlay>
							<Loader size={20} />
						</S.LoaderOverlay>
					</>
				);
			}
			return isImage ? renderImageThumbnail(previewUrl) : renderLoadingIndicator();
		}
		return isImage ? renderImageThumbnail(attachmentUrl) : renderIcon();
	};

	const renderText = () => {
		const displayName = PASTED_FILENAME_RE.test(attachment.filename)
			? 'Pasted content'
			: attachment.filename;
		return (
			<S.Text>
				<S.Filename>{displayName}</S.Filename>
				<S.FileType>{attachment.contentType}</S.FileType>
			</S.Text>
		);
	};

	return (
		<S.Base
			ref={ref}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onClick={onClick}
		>
			{renderLeftContent()}
			{renderText()}
			{isHovered && onDelete && (
				<S.DeleteIcon onClick={(e: React.MouseEvent) => e.stopPropagation()}>
					<SmallIconButton
						state={SmallIconButtonState.REGULAR}
						iconType={IconType.CLOSE}
						onClick={onDeleteClick}
					/>
				</S.DeleteIcon>
			)}
		</S.Base>
	);
};

export default AttachmentTile;
